import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { useEffect, useMemo, useState } from "react";

import { useOrderMarkets, useOrdersCount } from "@web-app/api";
import { Button, ConfirmModal, Icon, Input } from "@web-app/components";
import useDispatchStore, {
  getDefaultLocationForCountry,
} from "@web-app/store/dispatchStore";
import { AppFilterType, State } from "@web-app/types";

import MarketCountrySelector from "./MarketCountrySelector";
import MarketsList from "./MarketsList";
import SelectedMarketPills from "./SelectedMarketPills";

export default function MarketsDropdown({
  appFilter,
  setAppFilter,
}: {
  appFilter: AppFilterType;
  setAppFilter: (value: AppFilterType) => void;
}) {
  const optimizedOrdersCount = 2000;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [temporaryMarketIds, setTemporaryMarketIds] = useState<string[]>(
    appFilter.stateIds || [],
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [popoverClose, setPopoverClose] = useState<(() => void) | null>(null);
  const [shouldAutoSelectAll, setShouldAutoSelectAll] = useState(false);

  // Only fetch states when dropdown is open
  const { data: marketsMap, isSuccess } = useOrderMarkets({
    countryCode: appFilter.countryCode,
    enabled: isDropdownOpen,
  });

  useEffect(() => {
    setTemporaryMarketIds([]);
  }, [appFilter.countryCode]);

  // Auto-select all markets when country changes and states are loaded
  useEffect(() => {
    if (shouldAutoSelectAll && isSuccess && marketsMap) {
      const allStateIds = Array.from(marketsMap.values()).map(
        (state) => state.id,
      );
      setTemporaryMarketIds([...allStateIds, "null"]);
      setShouldAutoSelectAll(false);
    }
  }, [shouldAutoSelectAll, isSuccess, marketsMap]);

  const temporaryFilter = useMemo(
    () => ({
      ...appFilter,
      stateIds: temporaryMarketIds,
    }),
    [appFilter, temporaryMarketIds],
  );

  const { data: ordersCount, isSuccess: isOrdersCountSuccess } =
    useOrdersCount(temporaryFilter);

  const onMarketChange = (value: string, marketId: string) => {
    setTemporaryMarketIds((prev) => {
      // Check if this state has children
      const children = marketHierarchy.get(marketId);

      if (value === "true") {
        // Add the parent and all children
        const childIds = children?.map((child) => child.id) || [];
        return [...new Set([...prev, marketId, ...childIds])];
      } else {
        // Remove the parent and all children
        const childIds = children?.map((child) => child.id) || [];
        return prev.filter((id) => id !== marketId && !childIds.includes(id));
      }
    });
  };

  const onCountryChange = (countryCode: string) => {
    setAppFilter({
      ...appFilter,
      countryCode,
      stateIds: [], // Reset states when country changes
    });
    setShouldAutoSelectAll(true); // Auto-select all markets when country changes

    // Update map location to the default location for the new country
    const newLocation = getDefaultLocationForCountry(countryCode);
    useDispatchStore.setState({
      userLatestLocation: newLocation,
      userLatestZoomLevel: 10,
    });
  };

  const onLoadOrders = (close?: () => void) => {
    const orderCount = ordersCount?.totalCount || 0;

    if (orderCount > optimizedOrdersCount) {
      setPopoverClose(() => close);
      setShowConfirmModal(true);
      return;
    }

    setAppFilter({
      ...appFilter,
      stateIds: temporaryMarketIds,
    });
    close?.();
  };

  const handleConfirmLoad = () => {
    setAppFilter({
      ...appFilter,
      stateIds: temporaryMarketIds,
    });
    setShowConfirmModal(false);
    popoverClose?.();
    setPopoverClose(null);
  };

  const handleCancelLoad = () => {
    setShowConfirmModal(false);
    setPopoverClose(null);
  };

  const removeTemporaryMarket = (marketId: string) => {
    setTemporaryMarketIds((prev) => prev.filter((id) => id !== marketId));
  };

  const resetTemporaryMarket = () => {
    setTemporaryMarketIds(appFilter.stateIds || []);
    setSearchText("");
  };

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
    resetTemporaryMarket();
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setSearchText("");
  };

  // Only compute sorted states when dropdown is open
  const { sortedMarkets, marketHierarchy } = useMemo(() => {
    if (!isDropdownOpen || !marketsMap)
      return {
        marketHierarchy: new Map<string, State[]>(),
        sortedMarkets: [],
      };

    const statesArray = Array.from(marketsMap.values());

    let filteredStates = statesArray;

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchedStates = statesArray.filter((state) =>
        state.name.toLowerCase().includes(searchLower),
      );

      // Include parents of matched children
      const parentIdsToInclude = new Set<string>();
      matchedStates.forEach((state) => {
        if (state.parentId) {
          parentIdsToInclude.add(state.parentId);
        }
      });

      // Combine matched states with their parents
      const additionalParents = statesArray.filter(
        (state) =>
          parentIdsToInclude.has(state.id) && !matchedStates.includes(state),
      );

      filteredStates = [...matchedStates, ...additionalParents];
    }

    // Separate parents and children
    const parents = filteredStates.filter((state) => !state.parentId);
    const children = filteredStates.filter((state) => state.parentId);

    // Sort parents by name
    parents.sort((a, b) => a.name.localeCompare(b.name));

    // Build hierarchy map: parent id -> children
    const hierarchy = new Map<string, typeof statesArray>();
    parents.forEach((parent) => {
      const parentChildren = children
        .filter((child) => child.parentId === parent.id)
        .sort((a, b) => a.name.localeCompare(b.name));
      hierarchy.set(parent.id, parentChildren);
    });

    // Build hierarchical list: parent followed by its children
    const hierarchicalStates: typeof statesArray = [];
    parents.forEach((parent) => {
      hierarchicalStates.push(parent);
      hierarchicalStates.push(...(hierarchy.get(parent.id) || []));
    });

    return { marketHierarchy: hierarchy, sortedMarkets: hierarchicalStates };
  }, [isDropdownOpen, marketsMap, searchText]);

  const allFilteredStateIds = useMemo(() => {
    return [...sortedMarkets.map((state) => state.id), "null"];
  }, [sortedMarkets]);

  const isAllSelected =
    allFilteredStateIds.length > 0 &&
    allFilteredStateIds.every((id) => temporaryMarketIds.includes(id));
  const isPartiallySelected = temporaryMarketIds.length > 0 && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setTemporaryMarketIds([]);
    } else {
      setTemporaryMarketIds(allFilteredStateIds);
    }
  };

  const temporarySelectedCount = temporaryMarketIds.length;
  const selectedCount = appFilter.stateIds?.length || 0;

  return (
    <Popover className="relative">
      <PopoverButton
        onClick={handleDropdownOpen}
        className="rounded-md bg-white p-[5px] lg:px-3 lg:py-[8px] h-full text-xs font-semibold transition ease-in hover:bg-gray-800 hover:text-white focus:outline-none flex items-center gap-2"
      >
        <span className="flex gap-1 items-center">
          <span className="hidden lg:inline-block">Markets</span>
          <Icon name="globe" className="lg:hidden" />
          <span className="hidden relative lg:flex items-center gap-2">
            {selectedCount > 0 && (
              <span className="">({appFilter.stateIds.length})</span>
            )}
            <Icon name="fasChevronDown" className="h-3 w-3" />
          </span>
        </span>
      </PopoverButton>
      <Transition
        as="div"
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        className="absolute -right-[90px] md:left-auto md:right-0  2xl:right-auto 2xl:left-0 top-[45px] z-10 w-[360px] rounded-md bg-white shadow-xl"
        afterLeave={handleDropdownClose}
      >
        <PopoverPanel>
          {({ close }) => (
            <div className="py-3">
              <div className="mb-4">
                <MarketCountrySelector
                  selectedCountry={appFilter.countryCode ?? "US"}
                  onCountryChange={onCountryChange}
                />
                <div className="px-2">
                  <Input
                    type="search"
                    placeholder="Search"
                    value={searchText}
                    onChange={setSearchText}
                    className="my-3"
                  />
                </div>
              </div>
              {temporarySelectedCount > 0 && (
                <div className="mb-4 px-4">
                  <SelectedMarketPills
                    stateIds={temporaryMarketIds}
                    statesMap={marketsMap}
                    onRemoveState={removeTemporaryMarket}
                    variant="gray"
                    includeNullState={true}
                  />
                </div>
              )}
              {!isSuccess && (
                <div className="mb-4 text-[10px] lg:text-xs text-gray-500 flex justify-center">
                  <p>Fetching markets...</p>
                </div>
              )}
              {isSuccess && (
                <>
                  <div className="mb-2 lg:mb-3 px-4">
                    <Input
                      type="checkbox"
                      rightLabel="Select All"
                      value={isAllSelected ? "true" : "false"}
                      onChange={handleSelectAll}
                      className="font-medium"
                      indeterminate={isPartiallySelected}
                    />
                  </div>
                  <div className="border-t border-gray-200 pt-1"></div>
                </>
              )}
              {isSuccess && (
                <MarketsList
                  sortedStates={sortedMarkets}
                  stateHierarchy={marketHierarchy}
                  statesMap={marketsMap}
                  temporaryStateIds={temporaryMarketIds}
                  onChangeState={onMarketChange}
                  searchText={searchText}
                  includeNullState={true}
                />
              )}

              {/* Actions */}
              <div className="flex justify-between items-center border-t border-gray-200 pt-3 px-4">
                <span className="text-xs lg:text-sm text-gray-600">
                  {isOrdersCountSuccess
                    ? `${ordersCount?.totalCount} Orders`
                    : "Loading..."}
                </span>
                <Button onClick={() => onLoadOrders(close)} size="sm">
                  Load Orders
                </Button>
              </div>
            </div>
          )}
        </PopoverPanel>
      </Transition>
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Too many orders selected"
        subtitle={`You are about to load ${ordersCount?.totalCount || 0} orders. This may affect performance. Do you want to continue?`}
        onClose={handleCancelLoad}
        actions={[
          {
            label: "Cancel",
            onClick: handleCancelLoad,
            plain: true,
          },
          {
            label: "Load Orders",
            onClick: handleConfirmLoad,
          },
        ]}
      />
    </Popover>
  );
}
